/**
 * Persistent login-hint breadcrumb for silent SSO.
 *
 * The MSAL token cache lives in `sessionStorage` (see `msalInstance.ts`) — a
 * deliberate token-theft-blast-radius decision that also means every fresh tab
 * starts with no known account. To re-establish SSO without a button click,
 * boot-time `ssoSilent` needs a *target*: which identity to silently renew.
 *
 * This store keeps exactly that target — the account's `username` (UPN/email),
 * which is **not a secret and not a credential**. It is intentionally written
 * to `localStorage` so it survives the per-tab session cache. Nothing here can
 * authenticate anyone; the worst a leaked hint does is pre-fill an email on the
 * Microsoft sign-in page.
 *
 * Information dropped at this boundary is intentional: only the username
 * crosses, never tokens, claims, or account ids. All access is wrapped because
 * `localStorage` throws in private-mode / disabled-storage contexts; the
 * degraded state is simply "no hint" — boot then falls back to the sign-in
 * button rather than failing.
 */
const LOGIN_HINT_KEY = "einstein.auth.loginHint";

/**
 * Returns the persisted login hint, or `null` when absent or unreadable.
 * An empty stored value is treated as absent.
 */
export function readLoginHint(): string | null {
    try {
        const value = window.localStorage.getItem(LOGIN_HINT_KEY);
        return value !== null && value.length > 0 ? value : null;
    } catch {
        return null;
    }
}

/**
 * Records the signed-in account's username as the silent-SSO target.
 * No-ops on an empty username or when storage is unavailable.
 */
export function persistLoginHint(username: string): void {
    if (username.length === 0) {
        return;
    }
    try {
        window.localStorage.setItem(LOGIN_HINT_KEY, username);
    } catch {
        // Storage unavailable (private mode, quota, disabled): degrade to
        // "no hint" — the next cold tab shows the sign-in button instead of
        // attempting silent SSO. Never fatal.
    }
}

/**
 * Forgets the silent-SSO target. Called on explicit sign-out (so the user is
 * not silently re-authenticated against their will) and when a stale hint no
 * longer maps to a live session.
 */
export function clearLoginHint(): void {
    try {
        window.localStorage.removeItem(LOGIN_HINT_KEY);
    } catch {
        // Same degradation contract as persistLoginHint: a failed clear leaves
        // a hint that, at worst, drives one more no-op silent attempt.
    }
}
